import { action, computed, makeObservable } from "mobx";
import uuid from "react-native-uuid";
import type { Api } from "../../../core/apis";
import { BaseModel, type BaseModelData } from "../../../core/BaseModel";
import type { Stores } from "../../../core/stores";
import type { PlanInfoProps } from "../../../plans/PlanInfoBox";
import type { EditorValue } from "../../../ui/YooptaEditor";
import type { BusinessSettings } from "../../plan/BusinessPlanModel";
import type { BusinessScheduleItemSchedule } from "../../schedule/BusinessScheduleItem";
import type { CustomerProfileQuestion } from "../customers/BusinessCustomerModel";
import type { BusinessModuleTemplateData } from "./types";

export type BusinessTemplateScheduleItem = {
  uid: string;
  date: string | null;
  offsetInDays: number | null;
  emoji: string | null;
  title: string;
  description: EditorValue | null;
  schedule: BusinessScheduleItemSchedule[] | null;
};

export type BusinessTemplateSection = {
  uid: string;
  title: string;
  emoji: string | null;
  businessModules: BusinessModuleTemplateData[];
};

export interface BusinessTemplateData extends BaseModelData {
  customerUid: string;
  timesUsed: number;

  planInfo: PlanInfoProps; // Shared with sub planiras
  sections: BusinessTemplateSection[]; // Visible in menu
  schedule: BusinessTemplateScheduleItem[]; // Visible in menu

  businessModules: BusinessModuleTemplateData[];
  profileQuestions: CustomerProfileQuestion[];
  settings: BusinessSettings;
}

export class BusinessTemplateModel extends BaseModel<BusinessTemplateData> {
  constructor(stores: Stores, api: Api, data: BusinessTemplateData) {
    super(stores, api, data);

    makeObservable(this);

    Object.assign(this.data, {
      profileQuestions: this.data.profileQuestions ?? [],
      schedule: this.data.schedule ?? [],
    });

    if (!this.data.settings) {
      this.data.settings = {
        usePlanira: false,
        useIntroductionText: true,
        useLocale: "en",
      };
    }
  }

  @action async removeProfileQuestion(question: CustomerProfileQuestion) {
    this.data.profileQuestions = this.data.profileQuestions.filter(
      (q) => q.uid !== question.uid,
    );
    await this.api.businessTemplatesApi.set(this);
  }

  @action async addProfileQuestion(question: CustomerProfileQuestion) {
    if (this.data.profileQuestions.find((q) => q.uid === question.uid)) {
      return;
    }

    this.data.profileQuestions.push(question);
    await this.api.businessTemplatesApi.set(this);
  }

  @action async updateProfileQuestionsOrder(
    sections: CustomerProfileQuestion[],
  ) {
    this.data.profileQuestions = sections;
    await this.api.businessTemplatesApi.set(this);
  }

  async addBusinessScheduleItem(data: BusinessTemplateScheduleItem) {
    this.data.schedule.push(data);
    await this.api.businessTemplatesApi.set(this);
  }

  @action async updateScheduleItemsOrder(
    schedule: BusinessTemplateScheduleItem[],
  ) {
    this.data.schedule = schedule;
    await this.api.businessTemplatesApi.set(this);
  }

  async deleteBusinessTemplateScheduleItem(data: BusinessTemplateScheduleItem) {
    this.data.schedule = this.data.schedule.filter(
      (item) => data.uid !== item.uid,
    );
    await this.api.businessTemplatesApi.set(this);
  }

  async updateBusinessTemplateScheduleItem(item: BusinessTemplateScheduleItem) {
    const s = this.data.schedule.findIndex((s) => s.uid === item.uid);
    if (s !== undefined) {
      this.data.schedule[s] = item;
      await this.api.businessTemplatesApi.set(this);
    }
  }

  async addBusinessSection(sectionData: BusinessTemplateSection) {
    this.data.sections.push(sectionData);
    await this.api.businessTemplatesApi.set(this);
  }

  @action async updateSectionOrder(sections: BusinessTemplateSection[]) {
    this.data.sections = sections;
    await this.api.businessTemplatesApi.set(this);
  }

  async addBusinessTemplateModuleToSection(
    section: BusinessTemplateSection,
    templateData: Omit<BusinessModuleTemplateData, "uid">,
  ) {
    const s = this.data.sections.find((s) => s.uid === section.uid);
    if (s) {
      s.businessModules.push({
        ...templateData,
        uid: uuid.v4(),
      } as BusinessModuleTemplateData);
      await this.api.businessTemplatesApi.set(this);
    }
  }

  async deleteBusinessTemplateSection(templateData: BusinessTemplateSection) {
    this.data.sections = this.data.sections.filter(
      (section) => templateData.uid !== section.uid,
    );
    await this.api.businessTemplatesApi.set(this);
  }

  async deleteBusinessTemplateSectionModule(
    section: BusinessTemplateSection,
    moduleData: BusinessModuleTemplateData,
  ) {
    const s = this.data.sections.find((s) => s.uid === section.uid);
    if (s) {
      s.businessModules = s.businessModules.filter(
        (module) => module.uid !== moduleData.uid,
      );
      await this.api.businessTemplatesApi.set(this);
    }
  }

  async updateBusinessTemplateSectionModuleOrder(
    section: BusinessTemplateSection,
    modules: BusinessModuleTemplateData[],
  ) {
    const s = this.data.sections.find((s) => s.uid === section.uid);
    if (s) {
      s.businessModules = modules;
      await this.api.businessTemplatesApi.set(this);
    }
  }

  async updateBusinessTemplateSection(section: BusinessTemplateSection) {
    const s = this.data.sections.findIndex((s) => s.uid === section.uid);
    if (s !== undefined) {
      this.data.sections[s] = section;
      await this.api.businessTemplatesApi.set(this);
    }
  }

  async updateBusinessTemplateSectionModule(
    section: BusinessTemplateSection,
    templateData: BusinessModuleTemplateData,
  ) {
    const s = this.data.sections.find((s) => s.uid === section.uid);
    if (s) {
      const index = s.businessModules.findIndex(
        (module) => module.uid === templateData.uid,
      );

      if (index !== undefined) {
        s.businessModules[index] = templateData;
        await this.api.businessTemplatesApi.set(this);
      }
    }
  }

  async addBusinessTemplateModule(
    templateData: Omit<BusinessModuleTemplateData, "uid">,
  ) {
    this.data.businessModules.push({
      ...templateData,
      uid: uuid.v4(),
    } as BusinessModuleTemplateData);
    await this.api.businessTemplatesApi.set(this);
  }

  async updateBusinessTemplateModule(templateData: BusinessModuleTemplateData) {
    const index = this.data.businessModules.findIndex(
      (module) => module.uid === templateData.uid,
    );

    if (index !== undefined) {
      this.data.businessModules[index] = templateData;
      await this.api.businessTemplatesApi.set(this);
    }
  }

  async deleteBusinessTemplateModule(templateData: BusinessModuleTemplateData) {
    this.data.businessModules = this.data.businessModules.filter(
      (module) => templateData.uid !== module.uid,
    );
    await this.api.businessTemplatesApi.set(this);
  }

  @action async updateBusinessTemplateOrder(
    modules: BusinessModuleTemplateData[],
  ) {
    this.data.businessModules = modules;
    await this.api.businessTemplatesApi.set(this);
  }

  async updateBusinessTemplatePlanInfo(info: Partial<PlanInfoProps>) {
    if (info !== undefined) {
      this.data.planInfo = { ...this.data.planInfo, ...info };
      await this.api.businessTemplatesApi.set(this);
    }
  }

  async updateBusinessSettings(settings: Partial<BusinessSettings>) {
    this.data.settings = { ...this.data.settings, ...settings };
    await this.api.businessTemplatesApi.set(this);
  }

  @computed get customer() {
    const p = this.stores.businessCustomersStore.byId.get(
      this.data.customerUid,
    );
    if (!p) {
      throw Error("Customer not found");
    }
    return p;
  }

  toFireStore(): Omit<BusinessTemplateData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }
}
